/* Native scroll remains available when JavaScript is unavailable. */
if (!customElements.get('smartled-review-carousel')) {
  customElements.define('smartled-review-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.track = this.querySelector('.reviews-slider');
      this.cards = Array.from(this.querySelectorAll('.review-card'));
      this.controls = this.querySelector('.reviews-carousel__controls');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      if (!this.track || !this.controls || !this.cards.length) return;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.update(), { signal, passive: true });
      this.track.addEventListener('pointerdown', (event) => {
        this.cancelAnimation();
        this.pointerStart = { x: event.clientX, y: event.clientY, scroll: this.track.scrollLeft };
        this.didSwipe = false;
      }, { signal, passive: true });
      this.track.addEventListener('pointermove', (event) => {
        if (!this.pointerStart) return;
        if (Math.abs(event.clientX - this.pointerStart.x) > 8 || Math.abs(event.clientY - this.pointerStart.y) > 8) {
          this.didSwipe = true;
        }
      }, { signal, passive: true });
      this.track.addEventListener('pointercancel', () => { this.didSwipe = true; }, { signal, passive: true });
      this.track.addEventListener('wheel', () => this.cancelAnimation(), { signal, passive: true });
      this.track.addEventListener('keydown', (event) => {
        const card = event.target.closest('.review-card');
        if (card && this.dialog && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.openReview(card);
          return;
        }
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.scrollTo(event.key === 'Home' ? 0 : this.maximum());
        }
      }, { signal });

      this.setupDialog(signal);

      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.track);
      this.setAttribute('data-ready', '');
      this.layout();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.cancelAnimation();
      if (this.dialog?.open) this.dialog.close();
      this.restorePage();
      this.controller = null;
    }

    setupDialog(signal) {
      const dialog = this.querySelector('.reviews-dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      this.dialog = dialog;
      this.dialogClose = dialog.querySelector('[data-review-close]');
      this.dialogMeta = dialog.querySelector('[data-review-dialog-meta]');
      this.dialogTitle = dialog.querySelector('[data-review-dialog-title]');
      this.dialogBody = dialog.querySelector('[data-review-dialog-body]');
      this.cards.forEach((card) => {
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-haspopup', 'dialog');
        card.setAttribute('aria-controls', dialog.id);
        card.setAttribute('aria-label', card.dataset.reviewLabel || card.querySelector('.review-title')?.textContent || 'Review');
      });
      this.track.addEventListener('click', (event) => {
        const card = event.target.closest('.review-card');
        if (!card) return;
        // Do not turn a touch swipe or drag into an accidental popup.
        if (event.detail !== 0 && (this.didSwipe || (this.pointerStart && Math.abs(this.track.scrollLeft - this.pointerStart.scroll) > 8))) return;
        this.openReview(card);
      }, { signal });
      this.dialogClose.addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(dialog.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((element) => element.getClientRects().length > 0);
        const index = focusable.indexOf(document.activeElement);
        if (index < 0 || (event.shiftKey && index === 0) || (!event.shiftKey && index === focusable.length - 1)) {
          event.preventDefault();
          focusable[event.shiftKey ? focusable.length - 1 : 0]?.focus();
        }
      }, { signal });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
          dialog.close();
        }
      }, { signal });
      dialog.addEventListener('close', () => {
        this.restorePage();
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      }, { signal });
      this.setAttribute('data-modal-ready', '');
    }

    openReview(card) {
      if (!this.dialog || this.dialog.open) return;
      this.cancelAnimation();
      this.returnFocus = card;
      this.dialogMeta.replaceChildren(...['.review-header', '.review-stars']
        .map((selector) => card.querySelector(selector)?.cloneNode(true))
        .filter(Boolean));
      this.dialogTitle.textContent = card.querySelector('.review-title')?.textContent || '';
      this.dialogBody.textContent = card.querySelector('.review-content')?.textContent || '';
      this.savedRootOverflow = document.documentElement.style.overflow;
      this.savedBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.pageLocked = true;
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.dialogClose.focus({ preventScroll: true });
    }

    restorePage() {
      if (!this.pageLocked) return;
      document.documentElement.style.overflow = this.savedRootOverflow;
      document.body.style.overflow = this.savedBodyOverflow;
      this.pageLocked = false;
    }

    layout() {
      const width = this.track.clientWidth;
      if (!width) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const preferredColumns = window.innerWidth >= 1024
        ? 3
        : window.innerWidth <= 768
          ? 1
          : Math.max(1, Math.min(2, Math.floor((width + gap) / (200 + gap))));
      const columns = Math.min(this.cards.length, preferredColumns);
      if (this.layoutWidth === width && this.columns === columns && this.gap === gap) {
        this.update();
        return;
      }

      // Preserve the current review when resizing, but always finish on a full card.
      const previousStep = this.cardWidth ? this.cardWidth + this.gap : 1;
      const index = this.cardWidth ? Math.round(this.track.scrollLeft / previousStep) : 0;
      this.cancelAnimation();
      this.layoutWidth = width;
      this.columns = columns;
      this.gap = gap;
      this.cardWidth = (width - gap * (columns - 1)) / columns;
      this.style.setProperty('--review-card-width', `${this.cardWidth}px`);
      this.dataset.columns = columns;
      this.track.scrollTo({
        left: Math.min(index, this.cards.length - columns) * (this.cardWidth + gap),
        behavior: 'instant'
      });
      this.update();
    }

    maximum() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    update() {
      const maximum = this.maximum();
      this.controls.hidden = maximum < 2;
      this.previous.disabled = this.track.scrollLeft < 2;
      this.next.disabled = this.track.scrollLeft >= maximum - 2;
    }

    move(direction) {
      const current = this.destination ?? this.track.scrollLeft;
      const step = this.cardWidth + this.gap;
      const index = direction > 0
        ? Math.floor((current + 2) / step) + 1
        : Math.ceil((current - 2) / step) - 1;
      this.scrollTo(index * step);
    }

    scrollTo(left) {
      this.cancelAnimation();
      const start = this.track.scrollLeft;
      const target = Math.max(0, Math.min(left, this.maximum()));
      const distance = target - start;
      if (Math.abs(distance) < 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.track.scrollTo({ left: target, behavior: 'instant' });
        this.update();
        return;
      }

      // A consistent short slide, independent of the browser's native scroll duration.
      this.destination = target;
      this.setAttribute('data-animating', '');
      const duration = 380;
      const started = performance.now();
      const animate = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const eased = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        this.track.scrollLeft = start + distance * eased;
        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(animate);
        } else {
          this.track.scrollLeft = target;
          this.cancelAnimation();
          this.update();
        }
      };
      this.animationFrame = requestAnimationFrame(animate);
    }

    cancelAnimation() {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.destination = null;
      this.removeAttribute('data-animating');
    }
  });
}
